import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Modal for creating OR editing a subscription tier.
 * `initial` is a full tier doc (edit mode) or {} (create mode).
 *
 * Every numeric limit is a REQUIRED non-negative integer — the platform no
 * longer supports "Unlimited". Use a high value (e.g. 9999) to effectively
 * remove a cap while keeping enforcement predictable.
 */
export default function EditTierDialog({ initial, features, onClose, onSaved }) {
  const isNew = !initial?.tier_id;
  const [form, setForm] = useState(() => ({
    slug: initial.slug || "",
    name: initial.name || "",
    description: initial.description || "",
    sort_order: initial.sort_order ?? 100,
    default_trial_days: initial.default_trial_days ?? 0,
    currency: initial.currency || "SGD",
    price_monthly_cents: initial.price_monthly_cents ?? null,
    price_annually_cents: initial.price_annually_cents ?? null,
    limits: {
      // Default to sensible starter values; existing tiers keep their saved
      // numbers. Legacy `null` (Unlimited) rows are auto-migrated to 9999 on
      // startup, so we should never receive nulls here in practice.
      max_team_users: initial.limits?.max_team_users ?? 5,
      max_concurrent_projects: initial.limits?.max_concurrent_projects ?? 5,
      max_archived_projects: initial.limits?.max_archived_projects ?? 20,
      max_documents_per_project: initial.limits?.max_documents_per_project ?? 100,
    },
    features: initial.features || [],
  }));
  const [saving, setSaving] = useState(false);

  const featureState = useMemo(() => {
    // Ensure every feature in the registry has a row (default enabled=false).
    const byKey = Object.fromEntries((form.features || []).map((f) => [f.key, f.enabled]));
    return features.map((f) => ({
      ...f,
      enabled: byKey[f.key] ?? false,
    }));
  }, [features, form.features]);

  const setField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = { ...ref[parts[i]] };
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const toggleFeature = (key, enabled) => {
    setForm((prev) => {
      const others = (prev.features || []).filter((f) => f.key !== key);
      return { ...prev, features: [...others, { key, enabled }] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (isNew && !form.slug.trim()) return toast.error("Slug is required");
    setSaving(true);
    try {
      const body = {
        ...form,
        // Preserve full feature list keyed off the registry so the tier
        // record always mirrors the current known features.
        features: featureState.map((f) => ({ key: f.key, enabled: f.enabled })),
      };
      if (isNew) {
        await api.post("/superadmin/subscription-tiers", body);
        toast.success("Tier created");
      } else {
        // Slug is immutable after creation
        const { slug: _slug, ...patch } = body;
        await api.patch(`/superadmin/subscription-tiers/${initial.tier_id}`, patch);
        toast.success("Tier saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-tier-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isNew ? "Add subscription tier" : `Edit ${initial.name}`}</DialogTitle>
          <DialogDescription>
            Configure name, pricing, limits and feature toggles. Existing firms on this tier keep their current snapshot unless you re-apply.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6 mt-2">
          {/* Identity */}
          <section className="space-y-3">
            <SectionHeading>Identity</SectionHeading>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input
                  type="text" required
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  data-testid="tier-field-name"
                  className="input-soft"
                  placeholder="Pro Tier"
                />
              </Field>
              <Field label="Slug (machine key)" hint={isNew ? "e.g. pro, essential — immutable after creation" : "Slug is fixed once created"}>
                <input
                  type="text"
                  required={isNew}
                  disabled={!isNew}
                  value={form.slug}
                  onChange={(e) => setField("slug", e.target.value.toLowerCase())}
                  data-testid="tier-field-slug"
                  className="input-soft disabled:opacity-60"
                  placeholder="pro"
                />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                data-testid="tier-field-description"
                className="input-soft"
                placeholder="For growing firms coordinating multiple concurrent renovations."
              />
            </Field>
          </section>

          {/* Pricing */}
          <section className="space-y-3">
            <SectionHeading>Pricing</SectionHeading>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Currency">
                <select
                  value={form.currency}
                  onChange={(e) => setField("currency", e.target.value)}
                  data-testid="tier-field-currency"
                  className="input-soft"
                >
                  <option value="SGD">SGD</option>
                  <option value="USD">USD</option>
                  <option value="MYR">MYR</option>
                </select>
              </Field>
              <MoneyField
                label="Price / month"
                hint="Leave blank for 'Contact sales'"
                currency={form.currency}
                valueCents={form.price_monthly_cents}
                onChange={(cents) => setField("price_monthly_cents", cents)}
                testid="tier-field-price-monthly"
              />
              <MoneyField
                label="Price / year"
                hint="Optional annual price"
                currency={form.currency}
                valueCents={form.price_annually_cents}
                onChange={(cents) => setField("price_annually_cents", cents)}
                testid="tier-field-price-annually"
              />
            </div>
            <Field label="Default trial days">
              <input
                type="number" min={0} max={365}
                value={form.default_trial_days}
                onChange={(e) => setField("default_trial_days", Number(e.target.value) || 0)}
                data-testid="tier-field-trial-days"
                className="input-soft w-32"
              />
            </Field>
          </section>

          {/* Limits */}
          <section className="space-y-3">
            <SectionHeading>Limits</SectionHeading>
            <p className="text-xs text-[#7A7571]">
              Every limit must be a whole number. Use a high value (e.g. <span className="font-mono">9999</span>) to effectively remove a cap.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LimitField
                label="Max team users"
                value={form.limits.max_team_users}
                onChange={(v) => setField("limits.max_team_users", v)}
                testid="tier-limit-team-users"
              />
              <LimitField
                label="Max concurrent projects"
                value={form.limits.max_concurrent_projects}
                onChange={(v) => setField("limits.max_concurrent_projects", v)}
                testid="tier-limit-concurrent-projects"
              />
              <LimitField
                label="Max archived projects"
                value={form.limits.max_archived_projects}
                onChange={(v) => setField("limits.max_archived_projects", v)}
                testid="tier-limit-archived-projects"
              />
              <LimitField
                label="Max documents / project"
                value={form.limits.max_documents_per_project}
                onChange={(v) => setField("limits.max_documents_per_project", v)}
                testid="tier-limit-docs-per-project"
              />
            </div>
          </section>

          {/* Features */}
          <section className="space-y-3">
            <SectionHeading>Features</SectionHeading>
            {featureState.length === 0 ? (
              <p className="text-sm text-[#7A7571] italic">No features registered yet. Use <span className="font-semibold">Manage features</span> to add some.</p>
            ) : (
              <ul className="space-y-2">
                {featureState.map((f) => (
                  <li key={f.key} className="flex items-start justify-between gap-3 p-3 rounded-2xl bg-[#F7F4EC]">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#2C2A29]">{f.label}</div>
                      {f.description && <div className="text-xs text-[#7A7571] mt-0.5">{f.description}</div>}
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={f.enabled}
                        onChange={(e) => toggleFeature(f.key, e.target.checked)}
                        data-testid={`tier-feature-toggle-${f.key}`}
                        className="w-4 h-4"
                      />
                      <span className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
                        {f.enabled ? "Enabled" : "Off"}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#EBE5D9]">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="tier-cancel-btn">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="tier-save-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isNew ? "Create tier" : "Save changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------ tiny form helpers ------------

function SectionHeading({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">{children}</div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-[#4B4642] mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-[#7A7571] mt-1">{hint}</div>}
    </label>
  );
}

function LimitField({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs font-semibold text-[#4B4642] mb-1">{label}</div>
      <input
        type="number"
        min={0}
        required
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
        data-testid={testid}
        className="input-soft"
        placeholder="0"
      />
    </div>
  );
}

function MoneyField({ label, hint, currency, valueCents, onChange, testid }) {
  const display = valueCents == null ? "" : valueCents / 100;
  return (
    <div>
      <div className="text-xs font-semibold text-[#4B4642] mb-1">{label}</div>
      <div className="rounded-2xl border border-[#EBE5D9] bg-white flex items-stretch">
        <span className="px-3 flex items-center text-[#7A7571] text-sm border-r border-[#EBE5D9] font-mono">
          {currency}
        </span>
        <input
          type="number"
          min={0}
          step="0.01"
          value={display}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100))}
          data-testid={testid}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-[#2C2A29]"
          placeholder="0.00"
        />
      </div>
      {hint && <div className="text-[11px] text-[#7A7571] mt-1">{hint}</div>}
    </div>
  );
}
